import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { seconds } from '@wilkins-software/time-conversion-helpers';
import Cookies from 'js-cookie';
import { useEffect, useRef, useState } from 'react';
import { PRODUCT_NAME_BY_KEY } from '../../models';
import { TRACKING_EVENT_NAMES, ACTION_SUBJECT_IDS } from '../../utils/constants';
import { fetchAPI } from '../utils';
const mapSiteToEntitlements = async (site, createAnalyticsEvent, existingTransactionAccountId, existingInvoiceGroupId) => {
    let foundTransactionId = existingTransactionAccountId;
    let foundInvoiceGroupId = existingInvoiceGroupId;
    let entitlements = [];
    if (!site.products) {
        return [];
    }
    // iterate over products in site in order to find
    // products with same invoiceGroupId and ccpTransactionId
    for (const [productKey, product] of Object.entries(site.products)) {
        const { ccpTransactionAccountId, invoiceGroupId } = product;
        if (!invoiceGroupId ||
            !ccpTransactionAccountId ||
            !Object.keys(PRODUCT_NAME_BY_KEY).includes(productKey)) {
            continue;
        }
        // on finding first ids set them as those to look for
        if (!foundInvoiceGroupId && !foundTransactionId) {
            foundInvoiceGroupId = invoiceGroupId;
            foundTransactionId = ccpTransactionAccountId;
        }
        // return eligible entitlements in format required by UI
        entitlements = Object.entries(site.products)
            .filter(([productKey]) => 
        // only include Jira family and Confluence
        Object.keys(PRODUCT_NAME_BY_KEY).includes(productKey))
            .filter(
        // only products with ids matching the first set found
        ([_, { invoiceGroupId, ccpTransactionAccountId }]) => invoiceGroupId === foundInvoiceGroupId &&
            ccpTransactionAccountId === foundTransactionId)
            .map(([productKey, currentProduct]) => {
            const hostname = site.hostname ?? '';
            // strip hostname of protocol
            const siteURLMatch = /https:\/\/(.*)/.exec(hostname);
            const siteName = (siteURLMatch && siteURLMatch[1]) ?? '';
            const currDate = new Date();
            const utcZero = Date.UTC(currDate.getFullYear(), currDate.getMonth(), currDate.getDate());
            let daysLeftInTrial;
            if (currentProduct.trialEndDate) {
                daysLeftInTrial = seconds((new Date(currentProduct.trialEndDate).valueOf() -
                    new Date(utcZero).valueOf()) /
                    1000).toDays();
                if (daysLeftInTrial < 1) {
                    daysLeftInTrial = undefined;
                }
            }
            return {
                entitlementId: currentProduct.ccpEntitlementId,
                siteName,
                // get product name plus capitalized plan (aka 'edition')
                productAndPlanName: PRODUCT_NAME_BY_KEY[productKey] +
                    ' ' +
                    currentProduct.edition.slice(0, 1).toUpperCase() +
                    currentProduct.edition.slice(1),
                productKey: productKey,
                daysLeftInTrial
            };
        });
    }
    if (!foundTransactionId) {
        return [];
    }
    // if this is the first time we've found an invoice group id then
    // check that this invoice is not invoiceable, meaning payment details
    // have not yet been added
    if (!existingTransactionAccountId || !existingInvoiceGroupId) {
        const invoiceGroupResp = await fetchAPI(`/ccp/api/v1/invoice-groups/${foundInvoiceGroupId}/status`, {
            headers: { ['X-transaction-account']: foundTransactionId }
        });
        const { ok, status, statusText } = invoiceGroupResp;
        if (!ok) {
            console.error(`Call to /invoice-groups/ failed: ${status} ${statusText}`);
            createAnalyticsEvent({
                action: 'failed',
                actionSubject: 'Call to /invoice-groups/',
                actionSubjectId: ACTION_SUBJECT_IDS.wacCartInvoiceGroupsCallFailed,
                attributes: {
                    aaid: Cookies.get('__aid_user_id'),
                    error: `${status} ${statusText}`,
                    foundInvoiceGroupId
                }
            }).fire();
            return [];
        }
        const { invoiceable } = await invoiceGroupResp.json();
        if (invoiceable) {
            return [];
        }
    }
    return [
        entitlements,
        foundTransactionId,
        foundInvoiceGroupId,
        entitlements.length
    ];
};
const useEntitlements = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const hasRun = useRef(false);
    const [cachedEntitlements, setCachedEntitlements] = useState();
    const [totalEntitlementsCount, setTotalEntitlementsCount] = useState(0);
    const [transactionAccountId, setTransactionAccountId] = useState('');
    const [invoiceGroupId, setInvoiceGroupId] = useState('');
    useEffect(() => {
        if (hasRun.current) {
            return;
        }
        hasRun.current = true;
        void (async () => {
            try {
                const productHubInfo = await fetchAPI('/producthub/info');
                const { ok, status, statusText } = productHubInfo;
                if (!ok && status !== 401) {
                    console.error(`Call to /producthub/info failed with status ${status} ${statusText}`);
                    throw Error(`Call to /producthub/info failed with status ${status} ${statusText}`);
                }
                else if (status === 401) {
                    // user is not logged in so do nothing and log nothing
                    return;
                }
                const { sites } = await productHubInfo.json();
                if (!sites || sites.length === 0) {
                    createAnalyticsEvent({
                        eventName: TRACKING_EVENT_NAMES.noSitesFound,
                        action: 'returnedNoSites',
                        actionSubject: '/producthub/info',
                        actionSubjectId: ACTION_SUBJECT_IDS.wacCartProductHubInfoReturnedNoSites,
                        attributes: {
                            aaid: Cookies.get('__aid_user_id')
                        }
                    }).fire();
                    return;
                }
                let siteIdx = 0;
                let entitlements = [];
                let countTotalEntitlements = 0;
                let recvTransactionAccountId;
                let recvInvoiceGroupId;
                // iterate over sites to find set of matching
                // transaction account ids and invoice group ids
                while (siteIdx < sites.length) {
                    const site = sites[siteIdx];
                    if (site) {
                        const [foundEntitlements, taId, iGroupId, numEntitlements] = await mapSiteToEntitlements(site, createAnalyticsEvent, recvTransactionAccountId, recvInvoiceGroupId);
                        entitlements = entitlements.concat(foundEntitlements ?? []);
                        countTotalEntitlements += numEntitlements || 0;
                        recvTransactionAccountId = taId;
                        recvInvoiceGroupId = iGroupId;
                    }
                    siteIdx++;
                }
                if (entitlements.length === 0) {
                    createAnalyticsEvent({
                        eventName: TRACKING_EVENT_NAMES.noEntitlementsFound,
                        action: 'returnedNoEntitlements',
                        actionSubject: '/producthub/info',
                        actionSubjectId: ACTION_SUBJECT_IDS.wacCartProductHubInfoReturnedNoEntitlements,
                        attributes: {
                            aaid: Cookies.get('__aid_user_id')
                        }
                    }).fire();
                }
                setCachedEntitlements(entitlements);
                setTotalEntitlementsCount(countTotalEntitlements);
                setTransactionAccountId(recvTransactionAccountId ?? '');
                setInvoiceGroupId(recvInvoiceGroupId ?? '');
            }
            catch (error) {
                createAnalyticsEvent({
                    action: 'failed',
                    actionSubject: 'useEntitlements hook',
                    actionSubjectId: ACTION_SUBJECT_IDS.wacCartUseEntitlementsFailed,
                    attributes: {
                        aaid: Cookies.get('__aid_user_id'),
                        error: error instanceof Error ? error.message : JSON.stringify(error)
                    }
                }).fire();
            }
        })();
    });
    return [
        cachedEntitlements ?? [],
        transactionAccountId,
        invoiceGroupId,
        totalEntitlementsCount
    ];
};
export { useEntitlements };
//# sourceMappingURL=use-entitlements.js.map